import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import LandingChatWidget from '@/components/LandingChatWidget';
import { CookieSettingsButton } from '@/components/CookieConsent';
import { 
  ArrowRight, 
  CheckCircle, 
  Target, 
  Clock, 
  Trophy,
  Users,
  BookOpen,
  BarChart3,
  Play,
  GraduationCap,
  AlertCircle,
  Globe
} from 'lucide-react';

const API = (window.location.origin || '') + '/api';

const Landing = () => {
  const navigate = useNavigate();
  const { user, continueAsGuest } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTracks();
  }, []);

  const fetchTracks = async () => {
    try {
      const response = await fetch(`${API}/categories`);
      if (response.ok) {
        const data = await response.json();
        setTracks(data.categories || []);
      }
    } catch (error) {
      console.error('Failed to fetch tracks:', error);
      // Fallback to default tracks
      setTracks([
        { id: 'PMP', name: 'PMP', description: 'Project Management Professional', questions: 180, time: 230, is_available: true, group: 'PMI' },
        { id: 'CAPM', name: 'CAPM', description: 'Certified Associate in Project Management', questions: 150, time: 180, is_available: true, group: 'PMI' },
        { id: 'PSM', name: 'Scrum Master', description: 'Professional Scrum Master', questions: 80, time: 60, is_available: true, group: 'Scrum' },
        { id: 'PSPO', name: 'Product Owner', description: 'Professional Scrum Product Owner', questions: 80, time: 60, is_available: true, group: 'Scrum' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Images for tracks (with default fallback)
  const trackImages = {
    PMP: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&auto=format&fit=crop&q=60',
    CAPM: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&auto=format&fit=crop&q=60',
    PSM: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&auto=format&fit=crop&q=60',
    PSPO: 'https://images.unsplash.com/photo-1553028826-f4804a6dba3b?w=800&auto=format&fit=crop&q=60',
    ITIL4: 'https://images.unsplash.com/photo-1614508569207-3295ac89d75f?w=800&auto=format&fit=crop&q=60',
    PRINCE2: 'https://images.unsplash.com/photo-1758518732175-5d608ba3abdf?w=800&auto=format&fit=crop&q=60',
    SAFE: 'https://images.unsplash.com/photo-1758876202468-5ffe0ee61f07?w=800&auto=format&fit=crop&q=60',
    SAFe: 'https://images.unsplash.com/photo-1758876202468-5ffe0ee61f07?w=800&auto=format&fit=crop&q=60'
  };
  const defaultImage = 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&auto=format&fit=crop&q=60';

  // Helper function to convert hex color to gradient style
  const getTrackGradientStyle = (track) => {
    const color = track.color || '#64748b'; // fallback to slate-500
    // Create a darker shade for the gradient end
    const darkerColor = adjustColorBrightness(color, -30);
    return {
      background: `linear-gradient(to bottom right, ${color}, ${darkerColor})`
    };
  };

  // Helper to darken/lighten a hex color
  const adjustColorBrightness = (hex, percent) => {
    // Remove # if present
    hex = hex.replace(/^#/, '');
    
    // Parse hex to RGB
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    
    // Adjust brightness
    r = Math.max(0, Math.min(255, r + (r * percent / 100)));
    g = Math.max(0, Math.min(255, g + (g * percent / 100)));
    b = Math.max(0, Math.min(255, b + (b * percent / 100)));
    
    // Convert back to hex
    return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
  };

  const handleTryAsGuest = async (trackId) => {
    // Check if track is available
    const track = tracks.find(t => t.id === trackId);
    if (track && !track.is_available) {
      toast.error('This course is currently not available');
      return;
    }

    try {
      await continueAsGuest(trackId);
      toast.success(`Welcome! Explore your ${trackId} training options - 50 free questions!`);
      // Go to Dashboard instead of Training Mode directly
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to start guest session');
    }
  };

  const languages = [
    { code: 'us', name: 'English' },
    { code: 'de', name: 'German' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'cn', name: 'Chinese' },
    { code: 'ru', name: 'Russian' },
    { code: 'it', name: 'Italian' },
    { code: 'pl', name: 'Polish' },
    { code: 'se', name: 'Swedish' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'nl', name: 'Dutch' },
    { code: 'tr', name: 'Turkish' },
    { code: 'sa', name: 'Arabic' },
    { code: 'in', name: 'Hindi' },
    { code: 'cz', name: 'Czech' },
    { code: 'ro', name: 'Romanian' },
    { code: 'gr', name: 'Greek' },
    { code: 'dk', name: 'Danish' },
    { code: 'no', name: 'Norwegian' },
    { code: 'fi', name: 'Finnish' },
    { code: 'bg', name: 'Bulgarian' },
    { code: 'hr', name: 'Croatian' },
    { code: 'sk', name: 'Slovak' },
    { code: 'rs', name: 'Serbian' },
    { code: 'hu', name: 'Hungarian' },
    { code: 'ua', name: 'Ukrainian' }
  ];

  const features = [
    {
      icon: <Target className="w-6 h-6" />,
      title: 'Exam-style Questions',
      description: 'Questions that match the format, difficulty and logic of the real exam',
      bullets: ['Based on official exam guides', 'Regularly updated content', 'Multiple difficulty levels']
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Real Exam Conditions',
      description: 'Practice full mock exams under realistic time pressure',
      bullets: ['Timed mock exams', 'Same question count as real exam', 'Authentic exam interface']
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: 'Exam-focused Explanations',
      description: 'Understand why an answer is correct, not just which one',
      bullets: ['Detailed answer explanations', 'Reference to official sources', 'Learn from mistakes']
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Readiness Tracking',
      description: 'Check your performance and identify gaps before your exam day',
      bullets: ['Progress analytics', 'Weakness identification', 'Exam readiness score']
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: 'Study in Your Language',
      description: 'Study in your language. Think in exam logic! Available in 25 languages worldwide',
      bullets: ['25 languages available', 'Switch languages anytime', 'Same exam logic, your language']
    },
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: 'Multi-Exam Coverage',
      description: 'PMP, CAPM, Scrum & more - all certifications in one platform, with new exams added regularly',
      bullets: ['PMI & Scrum.org certifications', 'New exams added regularly', 'One platform, all exams']
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#0047AB] flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl tracking-tight">Exam Trainer</span>
            </div>
            <div className="flex items-center gap-4">
              {user ? (
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-[#0047AB] hover:bg-[#003380] text-white rounded-full px-6"
                  data-testid="dashboard-btn"
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/login')}
                    data-testid="login-btn"
                  >
                    Login
                  </Button>
                  <Button 
                    onClick={() => navigate('/register')}
                    className="bg-[#0047AB] hover:bg-[#003380] text-white rounded-full px-6"
                    data-testid="register-btn"
                  >
                    Register
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0047AB]/5 to-slate-100 dark:from-[#0047AB]/10 dark:to-slate-950" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNjB2NjBIMHoiLz48cGF0aCBkPSJNMzAgMzBtLTEgMGExIDEgMCAxIDAgMiAwYTEgMSAwIDEgMCAtMiAwIiBmaWxsPSIjMDA0N0FCIiBmaWxsLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-50" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0047AB]/10 text-[#0047AB] text-sm font-medium mb-6">
              <div className="w-8 h-8 rounded-lg bg-[#0047AB] flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              Built for Certification Exams
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 dark:text-white mb-6">
              <span className="text-slate-500">prepare.</span> <span className="text-slate-600">practice.</span> <span className="text-[#0047AB]">pass.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-600 dark:text-slate-400 mb-4 max-w-2xl mx-auto leading-relaxed">
              Prepare smarter. Practice realistically. Pass with confidence.
            </p>
            <p className="text-base md:text-lg text-slate-500 dark:text-slate-500 mb-10 max-w-2xl mx-auto leading-relaxed">
              Prepare for PMP®, CAPM®, Scrum Master & Product Owner certifications with realistic practice exams, instant feedback, 
              and comprehensive progress tracking.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => navigate(user ? '/dashboard' : '/register')}
                className="bg-[#0047AB] hover:bg-[#003380] text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-[#0047AB]/25 transition-all"
                data-testid="hero-get-started-btn"
              >
                Start Free Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('tracks').scrollIntoView({ behavior: 'smooth' })}
                className="rounded-full px-8 py-6 text-lg border-[#0047AB] text-[#0047AB] hover:bg-[#0047AB]/5"
                data-testid="view-tracks-btn"
              >
                View Courses
              </Button>
            </div>
            
            <div className="flex items-center justify-center gap-8 mt-12 text-sm text-slate-600 dark:text-slate-400">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Free Trial. No credit card required
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                800+ active Learners
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-[#0047AB]" />
                <span className="text-[#0047AB] font-medium">25 Languages</span>
              </div>
            </div>
          </div>
          
          {/* Trust Statement - Full width, matching hero styling */}
          <div className="mt-16 text-center max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-slate-600 dark:text-slate-300">
              Built on Structure and Designed for Success
            </h2>
            <p className="text-lg md:text-xl text-slate-600 dark:text-slate-400 leading-relaxed">
              Created by certified instructors and aligned with official certification frameworks.
              Each question undergoes structured quality review, cognitive level verification, and multi-stage validation to ensure originality, clarity, and exam-relevant accuracy.
            </p>
          </div>
        </div>
      </section>

      {/* Certification Tracks */}
      <section id="tracks" className="py-20 bg-slate-50 dark:bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              Choose Your Certification Path
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Select the certification that matches your career goals and experience level
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tracks.map((track) => (
              <Card 
                key={track.id} 
                className={`overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow group ${
                  !track.is_available ? 'opacity-75' : ''
                }`}
                data-testid={`track-${track.id}`}
              >
                <div 
                  className="h-40 relative overflow-hidden"
                  style={getTrackGradientStyle(track)}
                >
                  <img 
                    src={trackImages[track.id] || defaultImage} 
                    alt={track.name}
                    className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-50"
                  />
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <span className="text-2xl md:text-3xl font-bold text-white text-center leading-tight">{track.name}</span>
                  </div>
                  {!track.is_available && (
                    <div className="absolute top-2 right-2">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500 text-white text-xs font-medium">
                        <AlertCircle className="w-3 h-3" />
                        Coming Soon
                      </span>
                    </div>
                  )}
                </div>
                <CardContent className="p-5">
                  <h3 className="font-semibold text-base mb-1">{track.description}</h3>
                  <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400 mb-4">
                    <div className="flex items-center gap-1">
                      <BookOpen className="w-3 h-3" />
                      {track.questions} Questions
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {track.time} Min
                    </div>
                  </div>
                  {!track.is_available ? (
                    <div className="p-2 bg-amber-50 dark:bg-amber-950/20 rounded-lg text-center">
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        {track.unavailable_message || 'Coming Soon'}
                      </p>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 gap-1 text-xs"
                        onClick={() => handleTryAsGuest(track.id)}
                        data-testid={`try-${track.id}-btn`}
                      >
                        <Play className="w-3 h-3" />
                        Try Free
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 bg-[#0047AB] hover:bg-[#003380] text-xs"
                        onClick={() => navigate('/register')}
                      >
                        Register
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section - Social Proof */}
      <section className="py-20 bg-[#0047AB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">800+</div>
              <div className="text-white/70">Active Users</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">5000+</div>
              <div className="text-white/70">Practice Questions</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">92%</div>
              <div className="text-white/70">Reported Exam Success</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-bold mb-2">4.8</div>
              <div className="text-white/70">User Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-slate-900 dark:text-white">
              Everything You Need To Pass
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Comprehensive tools designed to simulate the real exam experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <Card 
                key={idx} 
                className="border-0 shadow-sm bg-slate-50 dark:bg-slate-800/50 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 group overflow-hidden"
                data-testid={`feature-card-${idx}`}
              >
                <CardContent className="p-8 h-full flex flex-col relative">
                  {/* Icon Badge */}
                  <div className="w-14 h-14 rounded-2xl bg-[#0047AB]/10 flex items-center justify-center text-[#0047AB] mb-6">
                    {feature.icon}
                  </div>
                  
                  {/* Title & Description */}
                  <h3 className="font-bold text-xl mb-3 text-slate-900 dark:text-white">{feature.title}</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-base leading-relaxed group-hover:hidden transition-opacity duration-200">{feature.description}</p>
                  
                  {/* Hover Bullet Points */}
                  <ul className="hidden group-hover:block text-slate-600 dark:text-slate-400 text-sm space-y-2 transition-opacity duration-200">
                    {feature.bullets.map((bullet, bulletIdx) => (
                      <li key={bulletIdx} className="flex items-start gap-2">
                        <span className="text-[#0047AB] mt-0.5">✓</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white dark:bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">
            Join thousands of professionals who have successfully achieved their certification with our platform.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-white text-[#0047AB] border-2 border-[#0047AB] hover:bg-blue-50 rounded-full px-10 py-6 text-lg font-semibold shadow-lg"
            data-testid="cta-register-btn"
          >
            Create Free Account
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-8">
            {/* Logo & Info */}
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#0047AB] flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <span className="font-semibold text-white">Exam Trainer</span>
              </div>
              <p className="text-sm">
                Your partner for professional exam preparation in project management.
              </p>
            </div>

            {/* Legal Links */}
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-6">
              <button 
                onClick={() => navigate('/terms')}
                className="text-sm hover:text-white transition-colors"
                data-testid="footer-terms-link"
              >
                Terms of Use
              </button>
              <button 
                onClick={() => navigate('/privacy-policy')}
                className="text-sm hover:text-white transition-colors"
                data-testid="footer-privacy-link"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => navigate('/cookie-policy')}
                className="text-sm hover:text-white transition-colors"
                data-testid="footer-cookie-link"
              >
                Cookie Policy
              </button>
              <button 
                onClick={() => navigate('/disclaimer')}
                className="text-sm hover:text-white transition-colors"
                data-testid="footer-disclaimer-link"
              >
                Disclaimer
              </button>
              {/* GDPR-compliant Cookie Settings link */}
              <CookieSettingsButton />
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-sm">
              © {new Date().getFullYear()} Margolytics Ltd. All rights reserved.
            </p>
            <p className="text-xs mt-2 text-slate-500">
              PMP®, CAPM® and PMI® are registered trademarks of the Project Management Institute, Inc.
            </p>
          </div>
        </div>
      </footer>

      {/* Landing Page Support Chatbot */}
      <LandingChatWidget />
    </div>
  );
};

export default Landing;
